<!--
/**
 * Self-contained card for a single bulk importer. Each card:
 *   - links to the sample CSV download
 *   - lets the user pick a CSV file
 *   - POSTs it to /api/v2/admin/bulk-import/{type}
 *   - shows the response counts (success / failed / email-failed)
 */
-->

<template>
  <div class="ensibuuko-bulk-import-card">
    <div class="ensibuuko-bulk-import-card__header">
      <oxd-text tag="p" class="ensibuuko-bulk-import-card__title">
        {{ importer.label }}
      </oxd-text>
      <a class="ensibuuko-bulk-import-card__sample" :href="sampleHref">
        {{ $t('general.download') }} template
      </a>
    </div>
    <oxd-text tag="p" class="ensibuuko-bulk-import-card__description">
      {{ importer.description }}
    </oxd-text>
    <oxd-form :loading="isLoading" @submit-valid="onUpload">
      <oxd-input-field
        v-model="attachment"
        type="file"
        :rules="rules"
        :button-label="$t('general.browse')"
        :placeholder="$t('general.no_file_selected')"
      />
      <div class="ensibuuko-bulk-import-card__actions">
        <oxd-button
          display-type="secondary"
          :label="$t('general.upload') || 'Upload'"
          type="submit"
        />
      </div>
    </oxd-form>
    <div v-if="result" class="ensibuuko-bulk-import-card__result">
      <oxd-text tag="p">
        Imported <strong>{{ result.success }}</strong> rows,
        <strong>{{ result.failed }}</strong> failed.
      </oxd-text>
      <oxd-text v-if="result.failedRows && result.failedRows.length" tag="p">
        Failed rows: {{ result.failedRows.join(', ') }}
      </oxd-text>
      <oxd-text
        v-if="result.emailFailedRows && result.emailFailedRows.length"
        tag="p"
      >
        Created but welcome email failed (hand the credentials over offline):
        rows {{ result.emailFailedRows.join(', ') }}
      </oxd-text>
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {
  required,
  maxFileSize,
  validFileTypes,
} from '@/core/util/validation/rules';

export default {
  name: 'BulkImportCard',
  props: {
    importer: {
      type: Object,
      required: true,
    },
    allowedFileTypes: {
      type: Array,
      required: true,
    },
    maxFileSize: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/admin/bulk-import/${props.importer.type}`,
    );
    return {http};
  },
  data() {
    return {
      attachment: null,
      isLoading: false,
      result: null,
      rules: [
        required,
        maxFileSize(this.maxFileSize),
        validFileTypes(this.allowedFileTypes),
      ],
    };
  },
  computed: {
    sampleHref() {
      return `${window.appGlobal.baseUrl}/admin/bulkImport/sample/${this.importer.type}`;
    },
  },
  methods: {
    onUpload() {
      if (!this.attachment) return;
      this.isLoading = true;
      this.result = null;
      this.http
        .create({attachment: this.attachment})
        .then((response) => {
          this.result = response.data.meta;
          if (this.result.failed === 0) {
            this.$toast.saveSuccess();
          } else {
            this.$toast.warn({
              title: this.$t('general.warning'),
              message: `Imported with ${this.result.failed} failed row(s).`,
            });
          }
          this.attachment = null;
        })
        .catch((err) => {
          this.$toast.warn({
            title: this.$t('general.warning'),
            message: err?.response?.data?.error?.message || 'Import failed',
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.ensibuuko-bulk-import-card {
  border: 1px solid rgba(143, 143, 143, 0.15);
  border-radius: 0.75rem;
  padding: 1rem;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.5rem;
  }

  &__title {
    font-weight: 700;
    font-size: 14px;
  }

  &__sample {
    font-size: 0.8rem;
    text-decoration: none;
  }

  &__description {
    font-size: 0.85rem;
    color: var(--oxd-interface-gray-darken-1-color, #64728c);
    margin-bottom: 0.25rem;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.5rem;
  }

  &__result {
    margin-top: 0.5rem;
    background-color: var(--oxd-interface-gray-lighten-2-color, #f6f5fb);
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.85rem;
  }
}
</style>
